/** Bitrix24 integration settings (Phase 9.1).
 *
 * Управляет:
 * - webhook URL (incoming, для outbound вызовов)
 * - outbound_token (для проверки входящих webhook от Bitrix)
 * - default_assigned_id (менеджер) — подгружается списком через user.get
 * - default_category_id (воронка) + stage_mapping (наш status → C{id}:NAME)
 * - enabled toggle
 *
 * Кнопки:
 * - «Проверить» → POST /settings/bitrix/test (вызывает user.get)
 * - «Загрузить менеджеров» → GET /settings/bitrix/users
 * - «Загрузить stages» → GET /settings/bitrix/stages → подставляет в mapping
 * - «Синхронизировать всё» → POST /settings/bitrix/sync-all
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, TestTube, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";

interface BitrixConfig {
  webhook_url: string;
  outbound_token: string;
  default_assigned_id: number;
  default_category_id: number;
  stage_mapping: Record<string, string>;
  enabled: boolean;
}

interface BitrixUser {
  id: number;
  name: string;
  email?: string;
  position?: string;
}

interface BitrixStage {
  status_id: string;
  name: string;
  sort: number | string;
}

interface BitrixCategory {
  id: number;
  name: string;
  sort: number;
}

const OUR_STATUSES = [
  "created",
  "paid",
  "confirmed",
  "packing",
  "shipped",
  "delivering",
  "delivered",
  "cancelled",
  "returned",
] as const;

const STATUS_LABEL_RU: Record<string, string> = {
  created: "Создан",
  paid: "Оплачен",
  confirmed: "Подтверждён",
  packing: "Собирается",
  shipped: "Отправлен",
  delivering: "Доставляется",
  delivered: "Доставлен",
  cancelled: "Отменён",
  returned: "Возврат",
};

export function BitrixSettings(): JSX.Element {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<BitrixConfig | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-bitrix-config"],
    queryFn: async () => (await api.get<BitrixConfig>("/settings/bitrix")).data,
  });

  useEffect(() => {
    if (data && !form) setForm(data);
  }, [data, form]);

  // Auto-load воронок, менеджеров и stages когда webhook_url настроен.
  // enabled-флаг гарантирует, что запрос не уйдёт впустую с пустым URL.
  const hasUrl = Boolean(form?.webhook_url);
  const categoryId = form?.default_category_id ?? 0;

  const usersQuery = useQuery({
    queryKey: ["admin-bitrix-users"],
    queryFn: async () =>
      (await api.get<BitrixUser[]>("/settings/bitrix/users")).data,
    enabled: hasUrl,
    staleTime: 5 * 60 * 1000,
  });

  const categoriesQuery = useQuery({
    queryKey: ["admin-bitrix-categories"],
    queryFn: async () =>
      (await api.get<BitrixCategory[]>("/settings/bitrix/categories")).data,
    enabled: hasUrl,
    staleTime: 5 * 60 * 1000,
  });

  const stagesQuery = useQuery({
    queryKey: ["admin-bitrix-stages", categoryId],
    queryFn: async () =>
      (
        await api.get<BitrixStage[]>("/settings/bitrix/stages", {
          params: { category_id: categoryId },
        })
      ).data,
    enabled: hasUrl,
    staleTime: 5 * 60 * 1000,
  });

  const users = usersQuery.data ?? null;
  const stages = stagesQuery.data ?? null;
  const categories = categoriesQuery.data ?? null;

  const saveMutation = useMutation({
    mutationFn: async (patch: Partial<BitrixConfig>) => {
      const { data } = await api.patch<BitrixConfig>("/settings/bitrix", patch);
      return data;
    },
    onSuccess: (data) => {
      toast.success("Сохранено");
      setForm(data);
      queryClient.invalidateQueries({ queryKey: ["admin-bitrix-config"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? "Ошибка сохранения"),
  });

  const testMutation = useMutation({
    mutationFn: async (url: string) => {
      const { data } = await api.post<{ ok: boolean; users_count?: number; error?: string }>(
        "/settings/bitrix/test",
        { webhook_url: url },
      );
      return data;
    },
    onSuccess: (data) => {
      if (data.ok) {
        toast.success(`Webhook работает (${data.users_count ?? 0} пользователей)`);
      } else {
        toast.error(`Ошибка: ${data.error}`);
      }
    },
    onError: () => toast.error("Не удалось проверить webhook"),
  });

  const syncAllMutation = useMutation({
    mutationFn: async () => (await api.post<{ synced: number }>("/settings/bitrix/sync-all")).data,
    onSuccess: (data) => toast.success(`Засинкано заказов: ${data.synced}`),
    onError: () => toast.error("Не удалось синхронизировать"),
  });

  if (isLoading || !form) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" /> загрузка…
      </div>
    );
  }

  function update<K extends keyof BitrixConfig>(field: K, value: BitrixConfig[K]) {
    if (!form) return;
    setForm({ ...form, [field]: value });
  }

  function updateMapping(status: string, stageId: string) {
    if (!form) return;
    setForm({ ...form, stage_mapping: { ...form.stage_mapping, [status]: stageId } });
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Подключение Bitrix24</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label>Webhook URL (incoming)</Label>
            <div className="flex gap-2">
              <Input
                placeholder="https://bestdigital.bitrix24.kz/rest/34/abc123/"
                value={form.webhook_url}
                onChange={(e) => update("webhook_url", e.target.value)}
                className="font-mono text-xs"
              />
              <Button
                variant="secondary"
                disabled={!form.webhook_url || testMutation.isPending}
                onClick={() => testMutation.mutate(form.webhook_url)}
              >
                {testMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <TestTube className="h-4 w-4" />
                )}
                Проверить
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Создаётся в Bitrix: Разработчикам → Готовые сценарии → Входящий вебхук.
              Нужны права <code>crm</code> и <code>user</code>.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Токен для входящих webhook (обратная синхронизация)</Label>
            <Input
              placeholder="оставьте пустым если не используете обратную синхронизацию"
              value={form.outbound_token}
              onChange={(e) => update("outbound_token", e.target.value)}
              className="font-mono text-xs"
            />
            <p className="text-xs text-muted-foreground">
              Нужен только если хочешь, чтобы изменения статуса сделки в Bitrix
              автоматически меняли статус заказа в нашей БД. Создаётся в Bitrix:
              Разработчикам → Готовые сценарии → <b>Исходящий вебхук</b>, URL{" "}
              <code>https://BACKEND-URL-КЛОНА.up.railway.app/api/v1/webhooks/bitrix/event</code>,
              события <code>ONCRMDEALUPDATE</code>. Bitrix покажет
              «код приложения» — скопируй сюда.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Воронка для новых заказов</Label>
              {categoriesQuery.isFetching && !categories ? (
                <div className="flex h-10 items-center px-3 text-sm text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> загрузка…
                </div>
              ) : (
                <Select
                  value={String(form.default_category_id)}
                  onValueChange={(v) =>
                    update("default_category_id", parseInt(v, 10))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите воронку" />
                  </SelectTrigger>
                  <SelectContent>
                    {(categories ?? []).map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.name}{" "}
                        <span className="ml-1 text-xs text-muted-foreground">
                          (C{c.id})
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <p className="text-[11px] text-muted-foreground">
                Все заказы с сайта/TMA/mobile попадут в эту воронку.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Ответственный менеджер</Label>
              {usersQuery.isFetching && !users ? (
                <div className="flex h-10 items-center px-3 text-sm text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> загрузка…
                </div>
              ) : (
                <Select
                  value={String(form.default_assigned_id)}
                  onValueChange={(v) =>
                    update("default_assigned_id", parseInt(v, 10))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите менеджера" />
                  </SelectTrigger>
                  <SelectContent>
                    {(users ?? []).map((u) => (
                      <SelectItem key={u.id} value={String(u.id)}>
                        {u.name}
                        {u.position && (
                          <span className="ml-1 text-xs text-muted-foreground">
                            — {u.position}
                          </span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <p className="text-[11px] text-muted-foreground">
                По умолчанию все новые сделки назначаются на этого сотрудника.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between rounded border p-3">
            <div>
              <div className="font-medium">Интеграция включена</div>
              <div className="text-xs text-muted-foreground">
                Заказы и клиенты автоматически синхронизируются в Bitrix.
              </div>
            </div>
            <Switch
              checked={form.enabled}
              onCheckedChange={(enabled) => update("enabled", enabled)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Маппинг статусов заказа → этапы Bitrix</CardTitle>
        </CardHeader>
        <CardContent>
          {stagesQuery.isFetching && !stages ? (
            <div className="flex items-center py-6 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> загрузка этапов воронки…
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-1/3">Наш статус заказа</TableHead>
                  <TableHead>Этап в Bitrix</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {OUR_STATUSES.map((status) => {
                  const stageId = form.stage_mapping[status] ?? "";
                  return (
                    <TableRow key={status}>
                      <TableCell>
                        <div className="font-medium">{STATUS_LABEL_RU[status]}</div>
                        <code className="text-xs text-muted-foreground">{status}</code>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={stageId || "__none__"}
                          onValueChange={(v) =>
                            updateMapping(status, v === "__none__" ? "" : v)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Выберите этап" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">
                              <span className="text-muted-foreground">
                                Не синхронизировать
                              </span>
                            </SelectItem>
                            {(stages ?? []).map((s) => (
                              <SelectItem key={s.status_id} value={s.status_id}>
                                {s.name}
                                <span className="ml-2 text-xs text-muted-foreground">
                                  {s.status_id}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-2">
        <Button onClick={() => form && saveMutation.mutate(form)} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-1 h-4 w-4" />
          )}
          Сохранить
        </Button>
        <Button
          variant="secondary"
          onClick={() => syncAllMutation.mutate()}
          disabled={!form.enabled || syncAllMutation.isPending}
        >
          {syncAllMutation.isPending ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCcw className="mr-1 h-4 w-4" />
          )}
          Синхронизировать все заказы
        </Button>
      </div>
    </div>
  );
}
